import type { components } from "@/lib/api/schema";

/** The API's run state (`RunSummary.state`, `Procedure.state`). */
export type ApiRunState = components["schemas"]["RunSummary"]["state"];
/** The library's per-machine flag. */
export type MachineFlag = NonNullable<components["schemas"]["LibraryMachine"]["flag"]>;

/** The UI's run states (`docs/design/tokens.md` §2). */
export type RunStateKey =
  | "processing"
  | "awaiting_review"
  | "needs_assignment"
  | "stalled"
  | "failed"
  | "ready"
  | "not_found";

export type Tone = "info" | "warn" | "danger" | "accent" | "neutral";

export interface RunStateSpec {
  /** Pill label (sentence case). */
  label: string;
  /** Whether a pill is rendered at all (ready runs show the Start button instead). */
  pill: boolean;
  tone: Tone;
  /** `ring` animates (static under reduced motion); `solid` is a plain dot. */
  dot: "ring" | "solid" | null;
}

/** One source of truth for run-state labels, pills and tones. */
export const RUN_STATES: Readonly<Record<RunStateKey, RunStateSpec>> = Object.freeze({
  processing: { label: "Processing", pill: true, tone: "info", dot: "ring" },
  needs_assignment: { label: "Needs assignment", pill: true, tone: "warn", dot: "solid" },
  awaiting_review: { label: "Needs review", pill: true, tone: "warn", dot: "solid" },
  stalled: { label: "Stalled", pill: true, tone: "warn", dot: "solid" },
  failed: { label: "Failed", pill: true, tone: "danger", dot: "solid" },
  ready: { label: "Ready", pill: false, tone: "accent", dot: null },
  not_found: { label: "Not found", pill: false, tone: "neutral", dot: null },
});

const API_TO_UI: Readonly<Record<ApiRunState, RunStateKey>> = Object.freeze({
  queued: "processing",
  running: "processing",
  pending: "processing",
  paused: "awaiting_review",
  stalled: "stalled",
  failed: "failed",
  done: "ready",
  needs_assignment: "needs_assignment",
});

/** Map an API run state to the UI state. */
export function runStateOf(state: ApiRunState): RunStateKey {
  return API_TO_UI[state];
}

/** Map a library machine flag to the UI state (`null` flag → ready). */
export function flagStateOf(flag: MachineFlag | null | undefined): RunStateKey {
  if (flag === "needs_review") return "awaiting_review";
  if (flag === "processing") return "processing";
  if (flag === "failed") return "failed";
  return "ready";
}

/** API states in which something is still working on the run (drives the 5 s poll). */
export function isActiveState(state: ApiRunState | null | undefined): boolean {
  return state === "queued" || state === "running" || state === "pending";
}
